import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../utils/api';

const OnboardingReview = () => {
  const [tenants, setTenants] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [query, setQuery] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});
  const statusLabels = {
    DRAFT: 'Borrador',
    SUBMITTED: 'Enviada',
    UNDER_REVIEW: 'En revision',
    APPROVED: 'Aprobada',
    REJECTED: 'Rechazada',
  };

  const loadPending = async () => {
    try {
      const res = await api.get('/ops/onboarding/pending');
      setTenants(res.data || []);
    } catch (error) {
      console.error('Error loading onboarding pending:', error);
      toast.error('Error al cargar pendientes');
    }
  };

  useEffect(() => {
    loadPending();
  }, []);

  const handleApprove = async () => {
    if (!selectedTenant) return;
    try {
      await api.post(`/ops/tenants/${selectedTenant.id}/onboarding/approve`);
      toast.success('Incorporacion aprobada');
      setApproveOpen(false);
      setSelectedTenant(null);
      loadPending();
    } catch (error) {
      console.error('Error approving onboarding:', error);
      toast.error('Error al aprobar');
    }
  };

  const openApprove = (tenant) => {
    setSelectedTenant(tenant);
    setApproveOpen(true);
  };

  const openReject = (tenant) => {
    setSelectedTenant(tenant);
    setRejectReason('');
    setRejectOpen(true);
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!selectedTenant || !rejectReason.trim()) return;
    try {
      await api.post(`/ops/tenants/${selectedTenant.id}/onboarding/reject`, {
        reason: rejectReason,
      });
      toast.success('Incorporacion rechazada');
      setRejectOpen(false);
      setSelectedTenant(null);
      loadPending();
    } catch (error) {
      console.error('Error rejecting onboarding:', error);
      toast.error('Error al rechazar');
    }
  };

  const handleReminder = async (tenant) => {
    try {
      await api.post(`/ops/tenants/${tenant.id}/onboarding/remind`);
      toast.success('Recordatorio enviado');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Error al enviar recordatorio');
    }
  };

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return tenants.filter((tenant) => {
      const matchesQuery =
        !normalized ||
        tenant.name?.toLowerCase().includes(normalized) ||
        tenant.id?.toLowerCase().includes(normalized);
      const matchesStatus =
        statusFilter === 'all' ? true : tenant.onboarding_status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [tenants, query, statusFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aDate = a.onboarding_submitted_at ? new Date(a.onboarding_submitted_at) : new Date(0);
      const bDate = b.onboarding_submitted_at ? new Date(b.onboarding_submitted_at) : new Date(0);
      if (aDate < bDate) return -1 * dir;
      if (aDate > bDate) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortDir]);

  const allSelected = sorted.length > 0 && sorted.every((t) => selected[t.id]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((tenant) => { next[tenant.id] = true; });
    setSelected(next);
  };
  const toggleRow = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleBulkReminder = async () => {
    const ids = sorted.filter((t) => selected[t.id]).map((t) => t.id);
    if (!ids.length) {
      toast.error('Selecciona al menos un tenant');
      return;
    }
    await Promise.all(ids.map((id) => api.post(`/ops/tenants/${id}/onboarding/remind`)));
    toast.success('Recordatorios enviados');
  };

  return (
    <div data-testid="ops-onboarding-page" className="max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Incorporaciones pendientes</h1>
          <p className="text-muted-foreground">Revisa y aprueba onboarding</p>
        </div>
        <Button variant="outline" onClick={handleBulkReminder}>Recordatorio masivo</Button>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por empresa o ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <div className="w-56">
            <Label>Filtro</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="SUBMITTED">Enviadas</SelectItem>
                <SelectItem value="UNDER_REVIEW">En revision</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <select
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="asc">Mas antiguas</option>
            <option value="desc">Mas recientes</option>
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="grid grid-cols-6 gap-2 border-b px-4 py-3 text-sm font-semibold">
            <div>
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
            </div>
            <div>Empresa</div>
            <div>Estado</div>
            <div>Enviado</div>
            <div>Horas</div>
            <div>Acciones</div>
          </div>
          {sorted.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">Sin pendientes.</div>
          ) : (
            sorted.map((tenant) => (
              <div key={tenant.id} className="grid grid-cols-6 gap-2 border-b px-4 py-3 text-sm">
                <div>
                  <input type="checkbox" checked={Boolean(selected[tenant.id])} onChange={() => toggleRow(tenant.id)} />
                </div>
                <div className="font-medium">{tenant.name}</div>
                <div>{statusLabels[tenant.onboarding_status] || tenant.onboarding_status}</div>
                <div>{tenant.onboarding_submitted_at || '-'}</div>
                <div>{tenant.hours_since_submitted ?? '-'}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => openApprove(tenant)}>Aprobar</Button>
                  <Button size="sm" variant="outline" onClick={() => openReject(tenant)}>
                    Rechazar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleReminder(tenant)}>
                    Recordatorio
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar incorporacion</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReject} className="space-y-4">
            <div>
              <Label>Motivo</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full">Rechazar</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar aprobacion</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Aprobar incorporacion de {selectedTenant?.name || 'este tenant'}?
          </p>
          <Button onClick={handleApprove} className="mt-4">Aprobar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingReview;
